import { AuthProvider, useAuth } from '../auth/AuthContext';
import { AuthGate } from '../auth/AuthGate';
import { TaskPane } from './TaskPane';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { NotificationProvider } from '../notifications/NotificationContext';
import { useSSEPrewarm } from '../chat/useSSE';

/**
 * Root component. Hierarchy:
 *   <AuthProvider>
 *     <AuthGate>           ← blocks on Auth0 session + backend profile
 *       <AuthedTaskPane>   ← pre-warms SSE the instant auth completes,
 *                            then renders TaskPane. Without the prewarm
 *                            SSE only opens when TaskPane mounts useChat,
 *                            adding 200-500ms of handshake latency to
 *                            the user's first send.
 *     </AuthGate>
 *   </AuthProvider>
 */
export function App(): JSX.Element {
  return (
    <AuthProvider>
      <AuthGate>
        <AuthedTaskPane />
      </AuthGate>
    </AuthProvider>
  );
}

function AuthedTaskPane(): JSX.Element | null {
  const { state } = useAuth();
  // Pre-warm SSE immediately on authentication. Hook is no-op if not
  // authenticated yet, idempotent if already connected. The connection is
  // reused by useSSE inside useChat — no duplicate EventSource.
  useSSEPrewarm();
  if (state.status !== 'authenticated') return null;
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <TaskPane user={state.user} />
      </NotificationProvider>
    </ErrorBoundary>
  );
}
